import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const phases = [
  {
    n: "I.",
    title: "Réponse 72 heures",
    duration: "3 jours",
    body:
      "Vous nous transmettez vos éléments. Nous vérifions les pièces, les délais et le greffe compétent. À la fin : une liste claire des documents et un calendrier de préparation.",
    detail: [
      "Inventaire des pièces financières et sociales à réunir",
      "Vérification des délais légaux et du formulaire applicable",
      "Identification du greffe compétent",
      "Calendrier de préparation, prise de date et dépôt",
    ],
  },
  {
    n: "II.",
    title: "Votre choix de procédure",
    duration: "3 à 10 jours",
    body:
      "Vous retenez la procédure qui vous convient. Nous préparons le cadre documentaire et les actes correspondants.",
    detail: [
      "Vous nous indiquez la procédure choisie",
      "Rédaction de la requête ou des actes demandés",
      "Constitution du dossier de pièces justificatives",
      "Coordination avec les professionnels que vous avez choisis",
    ],
  },
  {
    n: "III.",
    title: "Préparation & dépôt",
    duration: "Selon le greffe",
    body:
      "Nous rédigeons les actes, réunissons les pièces, demandons la date auprès du tribunal lorsque nécessaire et organisons le dépôt auprès du greffe compétent.",
    detail: [
      "Rédaction du dossier économique et des actes",
      "Contrôle de cohérence des pièces transmises",
      "Prise de date auprès du tribunal ou du greffe",
      "Dépôt et suivi de l'accusé d'enregistrement",
    ],
  },
  {
    n: "IV.",
    title: "Suivi du dossier",
    duration: "Jusqu'à confirmation",
    body:
      "Après le dépôt, nous suivons les accusés, les demandes de compléments et les confirmations jusqu'à la clôture de la formalité.",
    detail: [
      "Suivi de l'enregistrement auprès du greffe",
      "Transmission des demandes de complément",
      "Préparation des pièces additionnelles demandées",
      "Confirmation écrite de la formalité accomplie",
    ],
  },
];

export default function MethodePage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const barY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1a1918]" data-testid="methode-page">
      <header className="border-b border-[#1a1918]/15">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="font-serif-ed text-xl md:text-2xl">
            ProSecours
          </Link>
          <Link to="/" className="overline text-[#1a1918]/70 hover:text-[#1a1918]">
            ← Retour
          </Link>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-28 grid grid-cols-12 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="col-span-12 md:col-span-8 md:col-start-3"
        >
          <span className="overline text-[#1a1918]/60">§ Fonctionnement</span>
          <h1 className="mt-6 font-serif-ed text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
            Une discipline
            <br />
            en <span className="italic text-[#ce1e20]">quatre temps</span>.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#1a1918]/80 max-w-2xl leading-relaxed">
            Chaque dossier suit la même chorégraphie — parce qu'elle a prouvé sa
            robustesse sur plus de 120 dossiers de procédures collectives et de
            formalités juridiques.
          </p>
        </motion.div>
      </div>

      <section ref={ref} className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-24 md:pb-40">
        <div className="grid grid-cols-12 gap-4 relative">
          {/* Vertical rail */}
          <div className="hidden md:block absolute left-[calc(50%_-_0.5px)] top-0 bottom-0 w-px bg-[#1a1918]/15" />
          <motion.div
            style={{ height: barY }}
            className="hidden md:block absolute left-[calc(50%_-_1px)] top-0 w-[2px] bg-[#0f2846] origin-top"
          />

          <div className="col-span-12 md:col-span-10 md:col-start-2 space-y-24 md:space-y-40">
            {phases.map((p, i) => (
              <motion.div
                key={p.n}
                data-testid={`methode-phase-${i}`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, ease: [0.77, 0, 0.175, 1] }}
                className={`grid grid-cols-12 gap-4 md:gap-10 items-start ${
                  i % 2 === 0 ? "" : "md:direction-rtl"
                }`}
              >
                <div className={`col-span-12 md:col-span-5 ${i % 2 === 0 ? "" : "md:col-start-8"}`}>
                  <div className="chapter-num text-6xl md:text-8xl italic text-[#ce1e20]">
                    {p.n}
                  </div>
                  <h2 className="mt-2 font-serif-ed text-4xl md:text-5xl text-[#1a1918]">
                    {p.title}
                  </h2>
                  <div className="mt-3 overline text-[#1a1918]/60">
                    Durée typique · {p.duration}
                  </div>
                  <p className="mt-6 text-base md:text-lg text-[#1a1918]/80 leading-relaxed">
                    {p.body}
                  </p>
                </div>
                <div className={`col-span-12 md:col-span-5 ${i % 2 === 0 ? "md:col-start-8" : "md:col-start-2"}`}>
                  <ul className="border-t border-[#1a1918]/25 pt-6 space-y-3">
                    {p.detail.map((d, j) => (
                      <li
                        key={j}
                        className="flex items-baseline gap-3 text-base md:text-lg text-[#1a1918]/80"
                      >
                        <span className="chapter-num text-sm text-[#ce1e20]">
                          0{j + 1}
                        </span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-24 md:mt-32 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-8 md:col-start-3 border-t border-[#1a1918]/25 pt-10">
            <div className="overline text-[#1a1918]/60">Prochaine étape</div>
            <h3 className="mt-3 font-serif-ed text-3xl md:text-5xl leading-tight">
              Un premier échange, sans engagement.
            </h3>
            <Link
              to="/#audit"
              data-testid="methode-cta-audit"
              className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-[#1a1918] text-[#f7f5f0] overline hover:bg-[#ce1e20] transition-colors"
            >
              Décrire ma démarche →
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#1a1918]/15 py-8">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 overline text-[#1a1918]/50">
          © MMXXV ProSecours
        </div>
      </footer>
    </div>
  );
}
